import React, { useEffect } from 'react'
import { Dropdown, MenuProps } from 'antd'
import { useRecoilValue } from 'recoil'
import {
  Container,
  FullRowTd,
  RadioContent,
  HeadTd,
  IconSvg,
  DownIconSvg,
  MarkIconSvg,
  Information,
  Box,
  Table,
  Tbody,
  Td,
  Thead,
  Title,
  Tr,
  StyledRadioCard,
  StyledRadioCardGroup,
  HeadTitle,
  HeadPolicyNumber,
  HeadPolicyDate,
  DropdownText,
  HeadContent,
  DropdownItem,
  DropdownItemPolicyNumber,
  DropdownItemCover,
  DropdownItemCoverDetail,
  FormItem,
  InformationContainer,
  InformationText,
  InformationIconSvg,
  InformationBoxIcon,
} from './styles'
import CustomRadio from '@/components/common/CustomRadio'
import { PolicyDetailResponse } from '@/services/healthClaim/useGetClaimDetail/types'
import { formatLocaleDate } from '@/libs/utils/format-locale-date.util'
import healthClaimFormAtom from '@/recoil/healthClaimForm'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { IMAGE_URL } from '@/constant/IMAGE'
import { PolicyGroupEnum } from '@/constant/POLICY_GROUP'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'

interface policyItemInterface {
  key: string
  policyNumber: string
  cover: string
  dateRange: string
  marked: boolean
  policy?: PolicyDetailResponse
}

type PolicyFieldKey = keyof PolicyDetailResponse

const SelectClaimTreatmentType = () => {
  const [selectedType, setSelectedType] = React.useState<string>('')
  const healthClaim = useRecoilValue(healthClaimFormAtom)
  const isViewMode = healthClaim.mode === HealthClaimModeEnum.VIEW
  const {
    resetOnPolicyChange,
    selectedPolicy,
    selectedPolicyNumber,
    setSelectedPolicyOverride,
  } = useHealthClaimForm()

  const formatPolicyItems = (groups: string[]): policyItemInterface[] =>
    healthClaim.data?.policyDetails
      ?.filter((policy) => groups.includes(policy.policyGroup))
      .map((policy) => {
        const cover = `IPD ${policy.ipdCoverage || '-'} | OPD ${policy.opdCoverage || '-'} | PA ${
          policy.paCoverage ?? '-'
        } | ชดเชยรายได้ -`
        const effective = formatLocaleDate(policy.effectiveDate, 'th', 'D MMM BBBB')
        const expiry = formatLocaleDate(policy.expiryDate, 'th', 'D MMM BBBB')

        const isMarked = selectedPolicyNumber
          ? policy.policyNumber === selectedPolicyNumber
          : policy.isSelectClaim

        return {
          key: policy.policyNumber,
          policyNumber: policy.policyNumber,
          dateRange: `${effective} - ${expiry}`,
          marked: isMarked,
          cover,
          policy,
        }
      }) ?? []

  const healthPolicies = formatPolicyItems([PolicyGroupEnum.HEALTH, PolicyGroupEnum.ACTIVE_HEALTH])
  const accidentPolicies = formatPolicyItems([PolicyGroupEnum.PA])
  const hibPolicies = formatPolicyItems([PolicyGroupEnum.HIB])

  const isEmpty = [healthPolicies, accidentPolicies, hibPolicies].every((arr) => arr.length === 0)

  const selectedPolicies = {
    health:
      healthPolicies.length > 0
        ? healthPolicies.find((p) => p.marked) || healthPolicies[0]
        : undefined,
    accident:
      accidentPolicies.length > 0
        ? accidentPolicies.find((p) => p.marked) || accidentPolicies[0]
        : undefined,
    hib: hibPolicies.length > 0 ? hibPolicies.find((p) => p.marked) || hibPolicies[0] : undefined,
  }

  const benefitFields: { label: string; field: PolicyFieldKey }[] = [
    { label: 'วงเงินผู้ป่วยใน (IPD)', field: 'ipdCoverage' },
    { label: 'วงเงินผู้ป่วยนอก (OPD)', field: 'opdCoverage' },
    { label: 'วงเงินอุบัติเหตุ (PA)', field: 'paCoverage' },
    { label: 'วงเงินชดเชยนอน รพ.กรณีเจ็บป่วย', field: 'icHospitalSickness' },
    { label: 'วงเงินชดเชยนอน รพ.กรณีอุบัติเหตุ', field: 'icHospitalAccident' },
    { label: 'วงเงินชดเชยนอน รพ.ห้อง ICU', field: 'icSevereIllness' },
    {
      label: 'วงเงินชดเชยนอน รพ.จากการขับขี่/ซ้อนท้ายรถจักรยานยนต์',
      field: 'icMotorcyclePassenger',
    },
  ]

  const notCoverInformation =
    Object.values(selectedPolicies)
      .map((p) => p?.policy?.excludedDisease)
      .filter(Boolean)
      .join(' ') || '-'

  const benefits = benefitFields.map(({ label, field }) => ({
    label,
    values: [
      selectedPolicies.health?.policy?.[field] || '-',
      selectedPolicies.accident?.policy?.[field] || '-',
      selectedPolicies.hib?.policy?.[field] || '-',
    ],
  }))

  const renderPolicyDropdownItems = (array: policyItemInterface[]): MenuProps['items'] => {
    return array.flatMap((item, index) => [
      {
        label: (
          <DropdownItem>
            <Box>
              <DropdownItemPolicyNumber>{item.policyNumber}</DropdownItemPolicyNumber>
              <DropdownItemCover>{item.cover}</DropdownItemCover>
              <DropdownItemCoverDetail>คุ้มครองตั้งแต่ {item.dateRange}</DropdownItemCoverDetail>
            </Box>
            {item.marked && <MarkIconSvg src={IMAGE_URL.markIcon} />}
          </DropdownItem>
        ),
        key: item.key,
        onClick: () => selectPolicyForClaim(item.policyNumber),
      },
      index !== array.length - 1 ? { type: 'divider' } : null,
    ])
  }

  const ClaimOption = ({
    value,
    icon,
    title,
    policyItems,
  }: {
    value: string
    icon: string
    title: string
    policyItems: policyItemInterface[]
  }) => {
    const selected = policyItems.find((item) => item.marked)
    const disabled = isViewMode || policyItems?.length < 1
    const firstPolicy = policyItems[0]

    return (
      <HeadTd>
        <HeadContent>
          <RadioContent>
            <StyledRadioCard value={value} disabled={disabled}>
              <IconSvg src={icon} />
              <HeadTitle>{title}</HeadTitle>
              <HeadPolicyNumber>
                {selected?.policyNumber || firstPolicy?.policyNumber || null}
              </HeadPolicyNumber>
              {!disabled && (
                <Dropdown
                  menu={{ items: renderPolicyDropdownItems(policyItems) }}
                  trigger={['click']}
                  placement="bottom"
                >
                  <DropdownText onClick={(e) => e.preventDefault()}>
                    เปลี่ยน <DownIconSvg src={IMAGE_URL.caratDownIcon} />
                  </DropdownText>
                </Dropdown>
              )}
              {selected?.dateRange || firstPolicy?.dateRange ? (
                <HeadPolicyDate>{selected?.dateRange || firstPolicy?.dateRange}</HeadPolicyDate>
              ) : null}
            </StyledRadioCard>
          </RadioContent>
          <CustomRadio
            checked={selectedType === value}
            disabled={disabled}
            onClick={() => {
              setSelectedType(value)
              // Select the first policy of the selected type and deselect others
              if (policyItems.length > 0) {
                selectPolicyForClaim(policyItems[0].policyNumber)
              }
            }}
          />
        </HeadContent>
      </HeadTd>
    )
  }

  const getPolicyTypeFromPolicyGroup = (policyGroup?: string): string => {
    switch (policyGroup) {
      case 'Health':
      case 'Active Health':
        return 'health'
      case 'Personal Accident':
        return 'accident'
      case 'HIB':
        return 'hib'
      default:
        return ''
    }
  }

  const selectPolicyForClaim = (policyNumber: string) => {
    const newlySelected = healthClaim.data?.policyDetails?.find(
      (policy) => policy.policyNumber === policyNumber,
    )

    setSelectedPolicyOverride(policyNumber, { clearFallbacks: true })
    resetOnPolicyChange(policyNumber)

    setSelectedType(getPolicyTypeFromPolicyGroup(newlySelected?.policyGroup))
  }

  useEffect(() => {
    if (selectedPolicy) {
      setSelectedType(getPolicyTypeFromPolicyGroup(selectedPolicy.policyGroup) || '')
    } else {
      setSelectedType('')
    }
  }, [selectedPolicy])

  return (
    <Container>
      <Title>กรมธรรม์ที่ใช้เคลม</Title>
      {isEmpty ? (
        <InformationContainer>
          <InformationBoxIcon>
            <InformationIconSvg src={IMAGE_URL.policyManagementIcon} />
          </InformationBoxIcon>
          <InformationText>ไม่มีข้อมูล</InformationText>
        </InformationContainer>
      ) : (
        <>
          <FormItem name="claim">
            <StyledRadioCardGroup>
              <Table>
                <Thead>
                  <Tr>
                    <ClaimOption
                      value="health"
                      icon={IMAGE_URL.policyHealthClaimIcon}
                      title="สุขภาพ"
                      policyItems={healthPolicies}
                    />
                    <ClaimOption
                      value="accident"
                      icon={IMAGE_URL.policyAccidentClaimIcon}
                      title="อุบัติเหตุ"
                      policyItems={accidentPolicies}
                    />
                    <ClaimOption
                      value="hib"
                      icon={IMAGE_URL.policyIncomeCompensationClaimIcon}
                      title="ชดเชยรายได้"
                      policyItems={hibPolicies}
                    />
                  </Tr>
                </Thead>
                <Tbody>
                  {benefits.map((section, index) => (
                    <React.Fragment key={index}>
                      <Tr>
                        <FullRowTd colSpan={3}>{section.label}</FullRowTd>
                      </Tr>
                      <Tr>
                        {section.values.map((value, idx) => (
                          <Td key={idx}>{value as string}</Td>
                        ))}
                      </Tr>
                    </React.Fragment>
                  ))}
                </Tbody>
              </Table>
            </StyledRadioCardGroup>
          </FormItem>
          <Information>โรคที่ไม่คุ้มครอง : {notCoverInformation}</Information>
        </>
      )}
    </Container>
  )
}

export default SelectClaimTreatmentType
